import React, { useEffect, useState } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Label,
  Input,
  Button,
  Spinner,
} from 'reactstrap'
import BreadCrumb from 'Components/Common/BreadCrumb'
import { toast } from 'react-toastify'
import { fetchTimerSettings, updateTimerSettings } from 'helpers/backend_helper'

interface TimerFields {
  home_kvkk: number
  photo: number
  photo_selfie: number
  payment: number
  payment_cash: number
  payment_creditCard: number
  print_share: number
  photo_count: number
  phone_to_print: number
  photo_roll: number
  exit_page: number
}

const DEFAULTS: TimerFields = {
  home_kvkk: 60,
  photo: 600,
  photo_selfie: 240,
  payment: 120,
  payment_cash: 120,
  payment_creditCard: 120,
  print_share: 240,
  photo_count: 180,
  phone_to_print: 180,
  photo_roll: 240,
  exit_page: 60,
}

const formatMinutes = (seconds: number): string => {
  if (!seconds || seconds <= 0) return '0 dk 0 sn'
  const min = Math.floor(seconds / 60)
  const sec = seconds % 60
  if (min === 0) return `${sec} sn`
  if (sec === 0) return `${min} dk`
  return `${min} dk ${sec} sn`
}

interface TimerGroup {
  title: string
  icon: string
  fields: { key: keyof TimerFields; label: string }[]
}

const TIMER_GROUPS: TimerGroup[] = [
  {
    title: 'Genel',
    icon: 'mdi mdi-cog-outline',
    fields: [
      { key: 'home_kvkk', label: 'Ana Sayfa (KVKK Modal)' },
      { key: 'exit_page', label: 'Cikis Sayfasi' },
    ],
  },
  {
    title: 'Fotograf',
    icon: 'mdi mdi-camera',
    fields: [
      { key: 'photo', label: 'Fotograf Cekimi' },
      { key: 'photo_selfie', label: 'Selfie Sayfasi' },
      { key: 'photo_count', label: 'Fotograf Sayisi Secimi' },
      { key: 'phone_to_print', label: 'Telefondan Baski' },
      { key: 'photo_roll', label: 'Photoroll' },
    ],
  },
  {
    title: 'Odeme',
    icon: 'mdi mdi-credit-card-outline',
    fields: [
      { key: 'payment', label: 'Odeme Yontemi Secimi' },
      { key: 'payment_cash', label: 'Nakit Odeme' },
      { key: 'payment_creditCard', label: 'Kredi Karti Odeme' },
    ],
  },
  {
    title: 'Cikti',
    icon: 'mdi mdi-printer',
    fields: [
      { key: 'print_share', label: 'Yazdirma / Paylasma' },
    ],
  },
]

const TimerSettings = () => {
  document.title = 'Timer Settings | SocialBooth - Admin Dashboard'

  const [loading, setLoading] = useState<boolean>(true)
  const [saving, setSaving] = useState<boolean>(false)
  const [formData, setFormData] = useState<TimerFields>({ ...DEFAULTS })

  const loadSettings = async () => {
    try {
      setLoading(true)
      const response: any = await fetchTimerSettings()
      if (response.success && response.data) {
        const data = response.data
        setFormData({
          home_kvkk: data.home_kvkk ?? DEFAULTS.home_kvkk,
          photo: data.photo ?? DEFAULTS.photo,
          photo_selfie: data.photo_selfie ?? DEFAULTS.photo_selfie,
          payment: data.payment ?? DEFAULTS.payment,
          payment_cash: data.payment_cash ?? DEFAULTS.payment_cash,
          payment_creditCard: data.payment_creditCard ?? DEFAULTS.payment_creditCard,
          print_share: data.print_share ?? DEFAULTS.print_share,
          photo_count: data.photo_count ?? DEFAULTS.photo_count,
          phone_to_print: data.phone_to_print ?? DEFAULTS.phone_to_print,
          photo_roll: data.photo_roll ?? DEFAULTS.photo_roll,
          exit_page: data.exit_page ?? DEFAULTS.exit_page,
        })
      }
    } catch (e) {
      toast.error('Timer ayarlari yuklenemedi.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSettings()
  }, [])

  const handleChange = (key: keyof TimerFields, value: string) => {
    const numValue = parseInt(value) || 0
    setFormData((prev) => ({ ...prev, [key]: numValue }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const response: any = await updateTimerSettings(formData)
      if (response.success) {
        toast.success('Timer ayarlari basariyla guncellendi.')
        if (response.data) {
          const data = response.data
          setFormData({
            home_kvkk: data.home_kvkk ?? DEFAULTS.home_kvkk,
            photo: data.photo ?? DEFAULTS.photo,
            photo_selfie: data.photo_selfie ?? DEFAULTS.photo_selfie,
            payment: data.payment ?? DEFAULTS.payment,
            payment_cash: data.payment_cash ?? DEFAULTS.payment_cash,
            payment_creditCard: data.payment_creditCard ?? DEFAULTS.payment_creditCard,
            print_share: data.print_share ?? DEFAULTS.print_share,
            photo_count: data.photo_count ?? DEFAULTS.photo_count,
            phone_to_print: data.phone_to_print ?? DEFAULTS.phone_to_print,
            photo_roll: data.photo_roll ?? DEFAULTS.photo_roll,
            exit_page: data.exit_page ?? DEFAULTS.exit_page,
          })
        }
      } else {
        toast.error(response.msg || 'Timer ayarlari guncellenemedi.')
      }
    } catch (e) {
      toast.error('Timer ayarlari guncellenemedi.')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setFormData({ ...DEFAULTS })
  }

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title='Timer Settings' pageTitle='Settings' />

          {loading ? (
            <div className='text-center py-5'>
              <Spinner color='primary' />
              <p className='mt-2 text-muted'>Yukleniyor...</p>
            </div>
          ) : (
            <>
              <Row>
                {/* Sol sütun: Genel + Ödeme */}
                <Col xl={6}>
                  {TIMER_GROUPS.filter(g => g.title === 'Genel' || g.title === 'Odeme').map((group) => (
                    <Card key={group.title}>
                      <CardHeader>
                        <h5 className='card-title mb-0'>
                          <i className={`${group.icon} me-2`}></i>
                          {group.title}
                        </h5>
                      </CardHeader>
                      <CardBody>
                        {group.fields.map((field) => (
                          <div className='mb-3' key={field.key}>
                            <Label htmlFor={field.key} className='form-label fw-medium'>
                              {field.label}
                            </Label>
                            <div className='input-group'>
                              <Input
                                type='number'
                                id={field.key}
                                min={1}
                                max={3600}
                                value={formData[field.key]}
                                onChange={(e) => handleChange(field.key, e.target.value)}
                              />
                              <span className='input-group-text'>saniye</span>
                            </div>
                            <small className='text-muted'>
                              {formatMinutes(formData[field.key])} (varsayilan: {formatMinutes(DEFAULTS[field.key])})
                            </small>
                          </div>
                        ))}
                      </CardBody>
                    </Card>
                  ))}
                </Col>
                {/* Sağ sütun: Fotoğraf + Çıktı */}
                <Col xl={6}>
                  {TIMER_GROUPS.filter(g => g.title === 'Fotograf' || g.title === 'Cikti').map((group) => (
                    <Card key={group.title}>
                      <CardHeader>
                        <h5 className='card-title mb-0'>
                          <i className={`${group.icon} me-2`}></i>
                          {group.title}
                        </h5>
                      </CardHeader>
                      <CardBody>
                        {group.fields.map((field) => (
                          <div className='mb-3' key={field.key}>
                            <Label htmlFor={field.key} className='form-label fw-medium'>
                              {field.label}
                            </Label>
                            <div className='input-group'>
                              <Input
                                type='number'
                                id={field.key}
                                min={1}
                                max={3600}
                                value={formData[field.key]}
                                onChange={(e) => handleChange(field.key, e.target.value)}
                              />
                              <span className='input-group-text'>saniye</span>
                            </div>
                            <small className='text-muted'>
                              {formatMinutes(formData[field.key])} (varsayilan: {formatMinutes(DEFAULTS[field.key])})
                            </small>
                          </div>
                        ))}
                      </CardBody>
                    </Card>
                  ))}
                </Col>
              </Row>

              <div className='d-flex gap-2 mb-4'>
                <Button color='success' onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner size='sm' className='me-2' />
                      Kaydediliyor...
                    </>
                  ) : (
                    <>
                      <i className='mdi mdi-content-save me-1'></i>
                      Kaydet
                    </>
                  )}
                </Button>
                <Button color='warning' outline onClick={handleReset} disabled={saving}>
                  <i className='mdi mdi-refresh me-1'></i>
                  Varsayilana Sifirla
                </Button>
              </div>
            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  )
}

export default TimerSettings
